import { useCallback, useEffect, useRef, useState } from "react";
import { useMyPermissions } from "../../lib/permissions.js";
import { denialOrRawMessage } from "../../lib/denialReason.js";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";
import {
    createLangSmithIntegration,
    deleteLangSmithIntegration,
    listLangSmithProjects,
    replaceLangSmithKey,
    setLangSmithWatchedProjects,
} from "../../lib/langsmithClient.js";
import { NO_DIRECTORY, langsmithNeedsAttention, langsmithSteps, scopeCaptureStep, whenLabel } from "./model.js";
import {
    AsideSection,
    Panel,
    SourceLoadState,
    SourcePage,
    Step,
    Steps,
    Tabs,
    inputCls,
} from "../../components/setupSteps.jsx";
import { StatusPill, TONE } from "../../components/statusCard.jsx";
import Button from "../../components/ui/Button.jsx";
import Checkbox from "../../components/ui/Checkbox.jsx";
import Field from "../../components/ui/Field.jsx";
import { ErrorText, SaveStatus } from "../../components/ui/Notice.jsx";
import SearchInput from "../../components/ui/SearchInput.jsx";
import { SkeletonRows } from "../../components/ui/Skeleton.jsx";
import TableBar, { Count } from "../../components/ui/TableBar.jsx";

const NEW_TAB = "new";

function workspaceTone(integration) {
    if (langsmithNeedsAttention(integration)) return TONE.ATTENTION;
    if (integration.watchedProjects.length === 0 || integration.status !== "connected") return TONE.WAITING;
    return TONE.OK;
}

function ProjectPicker({ integration, onSaved, onCancel }) {
    const caps = useMyPermissions();
    // Read through a ref so a permission revalidation never re-reads the workspace.
    const capsRef = useRef(caps);
    capsRef.current = caps;
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState("");
    const [selection, setSelection] = useState(
        () => new Set(integration.watchedProjects.map((project) => project.projectId)),
    );
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        listLangSmithProjects(integration.id)
            .then((response) => {
                if (cancelled) return;
                setProjects(
                    (response?.projects ?? []).map((project) => ({
                        projectId: project?.projectId ?? project?.project_id ?? "",
                        displayName: project?.displayName ?? project?.display_name ?? "",
                    })),
                );
            })
            .catch((err) => {
                if (!cancelled) setError(denialOrRawMessage(err, capsRef.current));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [integration.id]);

    const toggle = useCallback((projectId) => {
        setSelection((current) => {
            const next = new Set(current);
            if (next.has(projectId)) next.delete(projectId);
            else next.add(projectId);
            return next;
        });
    }, []);

    const save = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            await setLangSmithWatchedProjects(integration.id, [...selection]);
            await onSaved();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [caps, integration.id, onSaved, selection]);

    const needle = filter.trim().toLowerCase();
    const visible = needle
        ? projects.filter((project) =>
              (project.displayName || project.projectId).toLowerCase().includes(needle),
          )
        : projects;
    const watchedById = new Map(integration.watchedProjects.map((project) => [project.projectId, project]));
    const unchanged =
        selection.size === watchedById.size && [...selection].every((projectId) => watchedById.has(projectId));
    const saveLabel = saving
        ? "Saving…"
        : selection.size === 0 && watchedById.size > 0
          ? "Stop watching all projects"
          : selection.size === 1
            ? "Watch 1 project"
            : `Watch ${selection.size} projects`;

    const stopAll = selection.size === 0 && watchedById.size > 0;

    return (
        <Panel className="grid">
            <div aria-busy={loading ? "true" : undefined} className="grid">
                {loading ? (
                    <div className="px-[12px]">
                        <SkeletonRows
                            rows={3}
                            cells={["w-[16px]", "w-[44%]"]}
                            rowClassName="grid h-[38px] grid-cols-[16px_minmax(0,1fr)] items-center gap-[10px]"
                            label="Reading the workspace…"
                        />
                    </div>
                ) : error && projects.length === 0 ? (
                    <div className="p-[12px]">
                        <ErrorText>{error}</ErrorText>
                    </div>
                ) : projects.length === 0 ? (
                    <p className="m-0 p-[12px] text-[12.5px] leading-[1.45] text-[color:var(--muted)]">
                        This workspace has no tracing projects yet. One appears here once an agent traces into
                        it.
                    </p>
                ) : (
                    <>
                        {projects.length > 6 ? (
                            <TableBar className="px-[12px]">
                                <SearchInput
                                    size="sm"
                                    label="Filter projects"
                                    value={filter}
                                    onChange={setFilter}
                                    placeholder="Filter projects"
                                    className="w-[220px] max-w-full"
                                />
                                <Count n={visible.length} one="project" />
                            </TableBar>
                        ) : null}
                        <ul className="m-0 grid max-h-[320px] list-none gap-[1px] overflow-y-auto p-[4px]">
                            {visible.length === 0 ? (
                                <li className="px-[8px] py-[8px] text-[12.5px] text-[color:var(--muted)]">No projects match.</li>
                            ) : null}
                            {visible.map((project) => {
                                const watched = watchedById.get(project.projectId);
                                const lastRun = whenLabel(watched?.lastWindowEnd);
                                return (
                                    <li key={project.projectId}>
                                        <Checkbox
                                            checked={selection.has(project.projectId)}
                                            onChange={() => toggle(project.projectId)}
                                            className="w-full rounded-[6px] px-[8px] py-[7px] hover:bg-[color-mix(in_srgb,var(--ink)_6%,transparent)]"
                                            label={<span className="grid min-w-0 gap-[2px]">
                                                <span className="[overflow-wrap:anywhere]">
                                                    {project.displayName || project.projectId}
                                                </span>
                                                {watched ? (
                                                    <span className="text-[12px] text-[color:var(--muted)]">
                                                        {lastRun ? `Runs received up to ${lastRun}.` : "Watched. Nothing delivered yet."}
                                                    </span>
                                                ) : null}
                                            </span>}
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    </>
                )}
            </div>
            {projects.length > 0 ? (
                <div className="grid gap-[10px] border-t border-[color:var(--line-soft)] p-[12px]">
                    <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">
                        Unticking a project deletes its automation rule in LangSmith and stops capture for it. Runs
                        already captured stay.
                    </p>
                    <div className="flex flex-wrap items-center justify-end gap-[8px]">
                        <SaveStatus error={error} />
                        {onCancel ? <Button onClick={onCancel}>Cancel</Button> : null}
                        <Button variant={stopAll ? "danger" : "primary"} onClick={save} disabled={saving || unchanged}>
                            {saveLabel}
                        </Button>
                    </div>
                </div>
            ) : null}
        </Panel>
    );
}

function ConnectSteps({ onCreated }) {
    const caps = useMyPermissions();
    const [apiKey, setApiKey] = useState("");
    const [workspaceId, setWorkspaceId] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const typed = apiKey.trim().length > 0;

    const create = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            await createLangSmithIntegration({ apiKey: apiKey.trim(), workspaceId: workspaceId.trim() });
            setApiKey("");
            setWorkspaceId("");
            await onCreated();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [apiKey, caps, onCreated, workspaceId]);

    const canCreate = typed && workspaceId.trim().length > 0 && !saving;

    return (
        <Steps label="Connect a LangSmith workspace">
            <Step
                number={1}
                state={typed ? "done" : "current"}
                title="Create a service key in LangSmith"
                summary="Scope it to one workspace. It needs to read runs and projects, create automation rules, and read organization members."
            />
            <Step
                number={2}
                state={typed ? "current" : "todo"}
                title="Paste the key and workspace ID"
                summary="The key must belong to this workspace or connecting fails."
            >
                <div className="grid gap-[14px]">
                    <Field label="Service key" hint="Stored encrypted and never shown again.">
                        <input
                            type="password"
                            value={apiKey}
                            autoComplete="off"
                            onChange={(event) => setApiKey(event.target.value)}
                            placeholder="lsv2_sk_..."
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Workspace ID" hint="LangSmith shows this under Settings, Workspaces.">
                        <input
                            type="text"
                            value={workspaceId}
                            spellCheck={false}
                            onChange={(event) => setWorkspaceId(event.target.value)}
                            placeholder="00000000-0000-0000-0000-000000000000"
                            className={inputCls}
                        />
                    </Field>
                    <div className="flex flex-wrap items-center justify-end gap-[8px]">
                        <SaveStatus error={error} />
                        <Button variant="primary" onClick={create} disabled={!canCreate}>
                            {saving ? "Connecting…" : "Connect workspace"}
                        </Button>
                    </div>
                </div>
            </Step>
            <Step
                number={3}
                state="todo"
                title="Choose tracing projects to watch"
                summary="Origin adds one automation rule to each project you pick."
            />
            <Step
                number={4}
                state="todo"
                title="Confirm the first run arrives"
                summary="LangSmith posts each finished run to Origin."
                last
            />
        </Steps>
    );
}

function KeyReplacement({ integration, onChanged, onClose }) {
    const caps = useMyPermissions();
    const [apiKey, setApiKey] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const replace = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            await replaceLangSmithKey(integration.id, apiKey.trim());
            onClose();
            await onChanged();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [apiKey, caps, integration.id, onChanged, onClose]);

    return (
        <div className="grid gap-[14px]">
            <Field
                label="Service key"
                hint="A key for the same workspace. Watched projects and their automation rules stay as they are."
            >
                <input
                    type="password"
                    value={apiKey}
                    autoComplete="off"
                    onChange={(event) => setApiKey(event.target.value)}
                    placeholder="lsv2_sk_..."
                    className={inputCls}
                />
            </Field>
            <div className="flex flex-wrap items-center justify-end gap-[8px]">
                <SaveStatus error={error} />
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="primary" onClick={replace} disabled={apiKey.trim().length === 0 || saving}>
                    {saving ? "Saving…" : "Replace key"}
                </Button>
            </div>
        </div>
    );
}

function WorkspaceSteps({ integration, directory, onChanged, refreshing }) {
    const [replacing, setReplacing] = useState(false);
    const {
        states: [, , projectsState, deliveryState],
        noDirectory,
    } = scopeCaptureStep(langsmithSteps(integration), directory);
    const watchedCount = integration.watchedProjects.length;
    const [picking, setPicking] = useState(watchedCount === 0);
    const lastPoll = whenLabel(integration.lastPollAt);

    const saved = useCallback(async () => {
        setPicking(false);
        await onChanged();
    }, [onChanged]);

    return (
        <Steps label={`LangSmith workspace ${integration.workspaceId}`}>
            <Step number={1} state="done" title="Create a service key in LangSmith" summary="Done." />
            <Step
                number={2}
                state={replacing ? "current" : "done"}
                title="Key saved"
                summary={
                    <>
                        Connected to workspace <span className="font-mono">{integration.workspaceId}</span>.
                    </>
                }
                action={
                    !replacing ? <Button onClick={() => setReplacing(true)}>Replace key</Button> : null
                }
            >
                {replacing ? (
                    <KeyReplacement
                        integration={integration}
                        onChanged={onChanged}
                        onClose={() => setReplacing(false)}
                    />
                ) : null}
            </Step>
            <Step
                number={3}
                state={picking && watchedCount > 0 ? "current" : projectsState}
                title="Choose tracing projects to watch"
                summary={
                    watchedCount === 0 ? (
                        "No projects watched, so nothing is captured from this workspace yet."
                    ) : (
                        <span className="inline-flex flex-wrap items-center gap-[8px]">
                            <StatusPill
                                tone={TONE.OK}
                                label={`Watching ${watchedCount} project${watchedCount === 1 ? "" : "s"}`}
                            />
                            <span>Each finished run in {watchedCount === 1 ? "it" : "them"} is captured.</span>
                        </span>
                    )
                }
                action={
                    !picking ? <Button onClick={() => setPicking(true)}>Change projects</Button> : null
                }
            >
                {picking ? (
                    <ProjectPicker
                        integration={integration}
                        onSaved={saved}
                        onCancel={watchedCount > 0 ? () => setPicking(false) : null}
                    />
                ) : null}
            </Step>
            <Step
                number={4}
                state={deliveryState}
                title={
                    noDirectory
                        ? NO_DIRECTORY.label
                        : deliveryState === "done"
                          ? "Capturing"
                          : "Confirm the first run arrives"
                }
                summary={
                    noDirectory
                        ? NO_DIRECTORY.detail
                        : deliveryState === "todo"
                          ? "Starts after you choose projects."
                          : deliveryState === "failed"
                            ? integration.lastSyncError || "LangSmith deliveries are failing."
                            : deliveryState === "done"
                              ? lastPoll
                                  ? `Last delivery ${lastPoll}.`
                                  : "Connected. No runs delivered yet."
                              : "Waiting for LangSmith's first delivery. It posts each run when it finishes."
                }
                action={
                    !noDirectory && (deliveryState === "current" || deliveryState === "failed") ? (
                        <Button onClick={onChanged} disabled={refreshing}>
                            {refreshing ? "Checking…" : "Refresh"}
                        </Button>
                    ) : null
                }
                last
            />
        </Steps>
    );
}

export default function LangSmithPage({
    source,
    health,
    entry,
    directory,
    onBack,
    onChanged,
    selectedItem,
    onSelectItem,
}) {
    const caps = useMyPermissions();
    const integrations = entry?.data ?? null;
    const [confirmRemove, setConfirmRemove] = useState(null);
    const [removing, setRemoving] = useState(false);
    const [error, setError] = useState(null);
    const knownIds = useRef(null);

    const selected =
        integrations && integrations.some((integration) => integration.id === selectedItem)
            ? selectedItem
            : integrations && integrations.length > 0 && selectedItem !== NEW_TAB
              ? integrations[0].id
              : NEW_TAB;
    const current = integrations?.find((integration) => integration.id === selected) ?? null;

    const created = useCallback(async () => {
        knownIds.current = new Set((integrations ?? []).map((integration) => integration.id));
        await onChanged();
    }, [integrations, onChanged]);

    // After a connect, move onto the workspace that just appeared.
    useEffect(() => {
        if (!knownIds.current || !integrations) return;
        const added = integrations.find((integration) => !knownIds.current.has(integration.id));
        knownIds.current = null;
        if (added) onSelectItem(added.id);
    }, [integrations, onSelectItem]);

    const remove = useCallback(async () => {
        const target = confirmRemove;
        setConfirmRemove(null);
        if (!target) return;
        setRemoving(true);
        setError(null);
        try {
            await deleteLangSmithIntegration(target.id);
            onSelectItem(null);
            await onChanged();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setRemoving(false);
        }
    }, [caps, confirmRemove, onChanged, onSelectItem]);

    if (!integrations) {
        return (
            <SourcePage source={source} health={health} onBack={onBack} backLabel="Back to data sources" busy={!entry?.loaded || entry?.loading} aside={null}>
                <SourceLoadState entry={entry} onRetry={onChanged} />
            </SourcePage>
        );
    }

    const tabs =
        integrations.length > 0 ? (
            <Tabs
                label="LangSmith workspaces"
                selected={selected}
                onSelect={onSelectItem}
                items={[
                    ...integrations.map((integration) => ({
                        id: integration.id,
                        label: integration.workspaceId,
                        mono: true,
                        dotTone: workspaceTone(integration),
                    })),
                    { id: NEW_TAB, label: "+ Connect workspace" },
                ]}
            />
        ) : null;

    return (
        <SourcePage
            source={source}
            health={health}
            onBack={onBack}
            backLabel="Back to data sources"
            busy={entry.loading}
            tabs={tabs}
            aside={
                <>
                    <AsideSection title="What Origin does in your workspace">
                        <ul className="m-0 grid list-disc gap-[4px] pl-[16px]">
                            <li>Reads runs and projects</li>
                            <li>Creates one automation rule per watched project</li>
                            <li>Reads organization members, to attribute runs to people</li>
                        </ul>
                    </AsideSection>
                    <AsideSection title="One key, one workspace">
                        <p className="m-0">Connect each workspace whose agents you want captured.</p>
                    </AsideSection>
                    {current ? (
                        <AsideSection title="Disconnect">
                            <p className="m-0">
                                Every automation rule Origin created in this workspace is deleted and capture stops.
                                Runs already captured are kept.
                            </p>
                            <Button variant="danger" onClick={() => setConfirmRemove(current)} disabled={removing} className="w-fit">
                                Disconnect
                            </Button>
                            <ErrorText>{error}</ErrorText>
                        </AsideSection>
                    ) : null}
                </>
            }
        >
            {current ? (
                <WorkspaceSteps
                    key={current.id}
                    integration={current}
                    directory={directory}
                    onChanged={onChanged}
                    refreshing={entry.loading}
                />
            ) : (
                <ConnectSteps onCreated={created} />
            )}
            <ConfirmDialog
                open={Boolean(confirmRemove)}
                analyticsId={DIALOG.LANGSMITH_DISCONNECT}
                eyebrow="LangSmith"
                title="Disconnect this workspace?"
                body="Every automation rule Origin created in this workspace is deleted and capture stops. Runs already captured are kept. Reconnecting means pasting the key again and picking the projects again."
                confirmLabel="Disconnect"
                destructive
                onConfirm={remove}
                onCancel={() => setConfirmRemove(null)}
            />
        </SourcePage>
    );
}
